// frontend/src/features/import/components/NewModelFlow.tsx
//
// Unified "New Model" flow used from Dashboard, Project Detail, or anywhere.
// Built-in template cards + user custom template cards (FR-003 Tier 1)
// → ProjectSelectorDialog → editor.
// "Describe with AI" → ProjectSelectorDialog → editor with FlowAssist auto-opened.
import { useState, useCallback, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { Star, Trash2 } from 'lucide-react'
import { formatVwu } from '@shared/hydraulic/physics/unit-converter'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useModelStore } from '../store/model-store'
import { ProjectSelectorDialog } from '@/features/workspace/components/ProjectSelectorDialog'
import {
  APPLICATION_TYPES,
  buildSystemForApplication,
  formatApplicationSpec,
} from '../utils/application-types'
import type { ApplicationType } from '../utils/application-types'
import { useCustomTemplates } from '../hooks/useCustomTemplates'
import { buildSystemFromCustomTemplate } from '../utils/template-snapshot'
import {
  CURRENT_TEMPLATE_SCHEMA_VERSION,
  type CustomTemplate,
} from '@shared/custom-templates/types'

/**
 * NewModelFlow lives outside the editor's UnitSystemProvider, so the
 * context hook returns its no-op default (US + gauge). Read the same
 * localStorage keys directly so the template card specs reflect whatever
 * the user picked last time they were in the editor. Defaults to US.
 */
const UNIT_SYSTEM_STORAGE_KEY = 'pf-connect-unit-system'
function getStoredUnitSystem(): 'US' | 'SI' {
  if (typeof window === 'undefined') return 'US'
  const stored = window.localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY)
  return stored === 'SI' ? 'SI' : 'US'
}

interface NewModelFlowProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewModelFlow({ open, onOpenChange }: NewModelFlowProps): JSX.Element {
  const navigate = useNavigate()
  const [selectedAppType, setSelectedAppType] = useState<ApplicationType | null>(null)
  const [selectedCustomTemplate, setSelectedCustomTemplate] = useState<CustomTemplate | null>(null)
  const [aiMode, setAiMode] = useState(false)
  // Grid basis is chosen here, on the way into the editor, and baked into the
  // system before it is saved. Not editable afterwards — see newDocumentHeader.
  const [basis, setBasis] = useState<'orthogonal' | 'isometric'>('orthogonal')
  // Sampled once per dialog open — no need to react to changes since the
  // user can't flip unit systems from inside this flow.
  const unitSystem = getStoredUnitSystem()
  const {
    templates: customTemplates,
    isLoading: customTemplatesLoading,
    error: customTemplatesError,
    remove: removeCustomTemplate,
    removePending: customTemplatesRemovePending,
  } = useCustomTemplates()

  const reset = useCallback((): void => {
    setSelectedAppType(null)
    setSelectedCustomTemplate(null)
    setAiMode(false)
    setBasis('orthogonal')
  }, [])

  const handleClose = useCallback((): void => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  const handleModelCreated = useCallback((projectId: string, modelId: string, version: number, system: unknown): void => {
    const wasAiMode = aiMode
    reset()
    onOpenChange(false)

    // Load the new system into the store BEFORE setting the IDs and navigating.
    //
    // Background: EditorPage's useEffect calls openModel(projectId, modelId)
    // on mount. openModel has an idempotency guard (model-store.ts:611) that
    // skips the fetch when `system !== null && currentProjectId === projectId
    // && currentModelId === modelId`. If we set just the IDs here without
    // resetting `system`, the previously-loaded model's data stays in the
    // store, the guard matches by accident, and the editor renders the
    // previous model until something else clears the cache.
    //
    // Calling loadFromSystem first replaces `system` with the new (empty/
    // template-derived) payload AND clears all the model-scoped state
    // (selection, undo, calculation results, etc.) — same payload we just
    // sent to the server, so no round trip needed. The subsequent setState
    // attaches the server-issued IDs and version.
    useModelStore.getState().loadFromSystem(system as Parameters<ReturnType<typeof useModelStore.getState>['loadFromSystem']>[0])
    useModelStore.setState({ projectId, modelId, serverVersion: version })

    if (wasAiMode) {
      // Open FlowAssist after navigation — the dialog will auto-send the pending message
      useModelStore.setState({
        showFlowAssistPalette: true,
        flowAssist: {
          ...useModelStore.getState().flowAssist,
          pendingMessage: "I just created a new model and want your help designing the piping system. Please ask me clarifying questions about what I need — system type, flow rates, pressures, number of devices, pipe materials, elevations, etc. Then build the system step by step.",
        },
      })
    }

    navigate(`/projects/${projectId}/models/${modelId}`)
  }, [aiMode, navigate, onOpenChange, reset])

  // For AI mode, use the Custom template (blank system with defaults)
  const customApp = APPLICATION_TYPES.find(a => a.id === 'custom')!

  return (
    <>
      {/* Step 1: Application type selector */}
      <Dialog
        open={open && !selectedAppType && !selectedCustomTemplate && !aiMode}
        onOpenChange={(o) => { if (!o) handleClose() }}
      >
        <DialogContent className="sm:max-w-[880px]">
          <DialogHeader>
            <DialogTitle>What type of system are you designing?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-2">
            {APPLICATION_TYPES.map((app): JSX.Element => {
              const Icon = app.icon
              const spec = formatApplicationSpec(app, unitSystem)
              return (
                <button
                  key={app.id}
                  onClick={() => {
                    if (app.id === 'ai-describe') {
                      setAiMode(true)
                    } else {
                      setSelectedAppType(app)
                    }
                  }}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                    app.id === 'ai-describe'
                      ? 'border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10'
                      : 'hover:border-primary hover:bg-muted/50',
                  )}
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{app.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{app.description}</p>
                    {spec && (
                      <p className="mt-1 whitespace-nowrap font-mono text-[10px] leading-tight text-muted-foreground/80">
                        {spec}
                      </p>
                    )}
                    {app.standards.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {app.standards.map((s): JSX.Element => (
                          <Badge key={s} variant="secondary" className="text-[9px] px-1.5 py-0">{s}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Custom templates (FR-003) — user-saved presets shared across
              the tenant. Rendered below the built-ins with a "Your team's
              templates" heading so they feel distinct but still discoverable.
              Cards with a mismatched schema version are greyed out with a
              tooltip explaining why. */}
          {customTemplatesLoading && (
            <>
              <div className="mt-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Star className="h-3 w-3" />
                Your team's templates
              </div>
              <div className="grid grid-cols-2 gap-2 py-2">
                {[0, 1].map((i): JSX.Element => (
                  <div
                    key={`skeleton-${i}`}
                    className="h-[68px] animate-pulse rounded-lg border border-amber-500/10 bg-amber-500/5"
                  />
                ))}
              </div>
            </>
          )}
          {!customTemplatesLoading && customTemplatesError && (
            <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-400">
              Couldn't load your team's templates: {customTemplatesError.message}
            </div>
          )}
          {!customTemplatesLoading && !customTemplatesError && customTemplates.length > 0 && (
            <>
              <div className="mt-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Star className="h-3 w-3" />
                Your team's templates
              </div>
              <div className="grid grid-cols-2 gap-2 py-2">
                {customTemplates.map((tpl): JSX.Element => {
                  const isUsable = tpl.payload.schema_version === CURRENT_TEMPLATE_SCHEMA_VERSION
                  return (
                    <div
                      key={tpl.id}
                      className={cn(
                        'group relative flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                        isUsable
                          ? 'border-amber-500/20 bg-amber-500/5 hover:border-amber-500/60 hover:bg-amber-500/10 cursor-pointer'
                          : 'border-muted-foreground/10 bg-muted/20 opacity-50 cursor-not-allowed',
                      )}
                      onClick={() => {
                        if (isUsable) setSelectedCustomTemplate(tpl)
                      }}
                      title={
                        isUsable
                          ? undefined
                          : 'Template was saved under an older schema. Delete it and recreate from a current model.'
                      }
                    >
                      <Star className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{tpl.name}</p>
                        {tpl.description && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {tpl.description}
                          </p>
                        )}
                        <p className="mt-1 whitespace-nowrap font-mono text-[10px] leading-tight text-muted-foreground/80">
                          {tpl.payload.fluid_zones.length} fluid zone
                          {tpl.payload.fluid_zones.length === 1 ? '' : 's'}
                          {' · '}
                          {tpl.payload.pipe_specifications.length} pipe spec
                          {tpl.payload.pipe_specifications.length === 1 ? '' : 's'}
                          {tpl.payload.design_constraints?.max_velocity && (
                            <>
                              {' · ≤ '}
                              {formatVwu(tpl.payload.design_constraints.max_velocity, 1)}
                            </>
                          )}
                        </p>
                      </div>
                      {/* Delete button — appears on hover, confirm-then-delete.
                          stopPropagation so clicking trash doesn't also fire the
                          card-click that would open the project selector. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (
                            window.confirm(
                              `Delete template "${tpl.name}"? This cannot be undone.`,
                            )
                          ) {
                            void removeCustomTemplate(tpl.id)
                          }
                        }}
                        disabled={customTemplatesRemovePending}
                        className="absolute right-2 top-2 hidden rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-red-500 hover:opacity-100 group-hover:block group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Delete template ${tpl.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Step 2a: Project selector after built-in template chosen */}
      <ProjectSelectorDialog
        open={selectedAppType !== null}
        onOpenChange={(o) => { if (!o) setSelectedAppType(null) }}
        system={selectedAppType ? buildSystemForApplication(selectedAppType, basis) : {}}
        sourceType="manual"
        defaultModelName={selectedAppType ? `New ${selectedAppType.label} Model` : 'New Model'}
        onModelCreated={handleModelCreated}
        onBasisChange={setBasis}
      />

      {/* Step 2c: Project selector after custom template chosen (FR-003).
          Uses sourceType 'manual' because custom templates are functionally
          identical to built-in templates — both produce a new blank model
          with prefilled config. */}
      <ProjectSelectorDialog
        open={selectedCustomTemplate !== null}
        onOpenChange={(o) => { if (!o) setSelectedCustomTemplate(null) }}
        system={
          selectedCustomTemplate
            ? buildSystemFromCustomTemplate(selectedCustomTemplate)
            : {}
        }
        sourceType="manual"
        defaultModelName={
          selectedCustomTemplate
            ? `New ${selectedCustomTemplate.name} Model`
            : 'New Model'
        }
        onModelCreated={handleModelCreated}
      />

      {/* Step 2b: Project selector for AI mode (blank Custom system, FlowAssist will guide) */}
      <ProjectSelectorDialog
        open={aiMode}
        onOpenChange={(o) => { if (!o) setAiMode(false) }}
        system={buildSystemForApplication(customApp, basis)}
        sourceType="manual"
        defaultModelName="New AI-Assisted Model"
        onModelCreated={handleModelCreated}
        onBasisChange={setBasis}
      />
    </>
  )
}
